import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Stack
} from '@mui/joy';
import { Close, Add } from '@mui/icons-material';
import { Note, PRIORITY_COLORS, PRIORITY_DISPLAY_NAMES, VISIBILITY_DISPLAY_NAMES } from '../types/note';
import { noteService } from '../services/noteService';
import NoteForm from './NoteForm';
import moment from 'moment';

interface NotesModalProps {
  open: boolean;
  onClose: () => void;
  date: string;
  onNoteCreated?: () => void;
  onNoteUpdated?: () => void;
  onNoteDeleted?: () => void;
}

const NotesModal: React.FC<NotesModalProps> = ({
  open,
  onClose,
  date,
  onNoteCreated,
  onNoteUpdated,
  onNoteDeleted
}) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchNotes = async () => {
    if (!open || !date) return;
    
    try {
      setLoading(true);
      setError(null);
      const notesData = await noteService.getNotesByDate(date);
      setNotes(notesData);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchNotes();
    }
  }, [open, date]);

  const handleCreateNote = async (data: any) => {
    try {
      await noteService.createNote({
        ...data,
        date
      });
      await fetchNotes();
      setFormOpen(false);
      onNoteCreated?.();
    } catch (err) {
      console.error('Error creating note:', err);
      throw err;
    }
  };

  const handleUpdateNote = async (data: any) => {
    if (!editingNote) return;
    
    try {
      await noteService.updateNote(editingNote.id, data);
      await fetchNotes();
      setEditingNote(null);
      setFormOpen(false);
      onNoteUpdated?.();
    } catch (err) {
      console.error('Error updating note:', err);
      throw err;
    }
  };

  const handleDeleteNote = async (note: Note) => {
    try {
      await noteService.deleteNote(note.id);
      await fetchNotes();
      onNoteDeleted?.();
    } catch (err) {
      console.error('Error deleting note:', err);
      throw err;
    }
  };

  const handleEditNote = (note: Note) => {
    setEditingNote(note);
    setFormOpen(true);
  };

  const handleAddNote = () => {
    setEditingNote(null);
    setFormOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    if (editingNote) {
      await handleUpdateNote(data);
    } else {
      await handleCreateNote(data);
    }
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format('dddd, MMMM D, YYYY');
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'important':
        return '⚠️';
      case 'urgent':
        return '🚨';
      default:
        return '📝';
    }
  };

  const getVisibilityIcon = (visibility: string) => {
    return visibility === 'public' ? '👁️' : '🔒';
  };

    return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalDialog size="lg" sx={{ maxWidth: 600, width: '100%' }}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography level="h4">
                Notes for {formatDate(date)}
              </Typography>
              <Button
                variant="plain"
                color="neutral"
                onClick={onClose}
                startDecorator={<Close />}
              >
                Close
              </Button>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert color="danger" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Button
                    startDecorator={<Add />}
                    onClick={handleAddNote}
                    sx={{ mb: 2 }}
                  >
                    Add Note
                  </Button>
                  
                  {notes.length === 0 ? (
                    <Typography level="body-md" color="neutral" textAlign="center" py={4}>
                      No notes for this date
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {notes.map((note) => (
                        <Box
                          key={note.id}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 'sm',
                            backgroundColor: 'background.level1'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography level="body-sm">
                                {getPriorityIcon(note.priority)}
                              </Typography>
                              <Chip
                                size="sm"
                                variant="soft"
                                sx={{
                                  backgroundColor: PRIORITY_COLORS[note.priority],
                                  color: 'white',
                                  fontSize: '0.625rem'
                                }}
                              >
                                {PRIORITY_DISPLAY_NAMES[note.priority]}
                              </Chip>
                              <Chip
                                size="sm"
                                variant="outlined"
                                sx={{ fontSize: '0.625rem' }}
                              >
                                {getVisibilityIcon(note.visibility)} {VISIBILITY_DISPLAY_NAMES[note.visibility]}
                              </Chip>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="sm"
                                variant="outlined"
                                onClick={() => handleEditNote(note)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outlined"
                                color="danger"
                                onClick={() => handleDeleteNote(note)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                          
                          {note.title && (
                            <Typography level="body-lg" fontWeight="md" sx={{ mb: 1 }}>
                              {note.title}
                            </Typography>
                          )}
                          
                          <Typography level="body-md" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                            {note.content}
                          </Typography>
                          
                          <Typography level="body-xs" color="neutral">
                            by {note.creator_name} • {moment(note.created_at).format('MMM D, YYYY h:mm A')}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
      
      {/* Note Form Modal */}
      <NoteForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingNote(null);
        }}
        onSubmit={handleFormSubmit}
        note={editingNote || undefined}
        selectedDate={date}
      />
    </>
  );
};

export default NotesModal; 